import type { IAppSummary } from '~/types/application'
import type { IPlaceholderDefaultValue, ITemplateConfigOptions } from '~/types/template'
import type { ITranslationKeyValue } from '~/types/translation'

import { useTranslationValuesQuery } from '~/queries/translation'
import { getTemplateConfigs } from '~/utils/template'

const getTemplateConfigWithTranslationValues = (
  template: ITemplateConfigOptions,
  translationData: ITranslationKeyValue[]
): ITemplateConfigOptions => {
  const templateConfigWithTranslationValues = template.config.map(({ placeholders, ...pageConfig }) => {
    const placeholdersWithTranslationValues = placeholders.map(({ defaultValue, ...placeholder }) => {
      if (defaultValue?.translation) {
        const translationKeyValue = translationData.find(({ key }) => key === defaultValue.translation)
        if (translationKeyValue) {
          return {
            ...placeholder,
            defaultValue: {
              ...defaultValue,
              translation: {
                label: defaultValue.translation,
                text: translationKeyValue.value,
                translation: translationKeyValue,
                value: defaultValue.translation,
              },
            } as IPlaceholderDefaultValue,
          }
        }
      }
      return { ...placeholder, defaultValue }
    })
    return { ...pageConfig, placeholders: placeholdersWithTranslationValues }
  })
  return { ...template, config: templateConfigWithTranslationValues }
}

export const useTemplateConfigWithTranslationValues = ({
  applicationSummary,
  templateKey,
}: {
  applicationSummary: IAppSummary
  templateKey: string
}): { isLoading: boolean; templateConfig: ITemplateConfigOptions | undefined } => {
  const template = getTemplateConfigs().find(({ key }) => templateKey === key)

  const defaultTranslationKeys = template?.config.reduce((keys, pageConfig) => {
    pageConfig.placeholders.forEach(({ defaultValue }) => {
      if (defaultValue?.translation && typeof defaultValue?.translation === 'string') {
        keys.push(defaultValue?.translation)
      }
    })
    return keys
  }, [] as string[])

  const defaultTranslationKeysSet = Array.from(new Set(defaultTranslationKeys))

  const { data: translationData, isLoading } = useTranslationValuesQuery({
    application: applicationSummary,
    translationKeys: defaultTranslationKeysSet,
  })

  const templateConfig =
    template && translationData ? getTemplateConfigWithTranslationValues(template, translationData) : template

  return { isLoading: isLoading && !!defaultTranslationKeysSet.length, templateConfig }
}
